import { useEffect, useRef, useState } from "react";
import { Headphones, Play, Pause, RotateCcw, RotateCw } from "lucide-react";

const NAVY = "#1a2332";
const NAVY_2 = "#111a26";
const GOLD = "#c9a961";
const GOLD_SOFT = "rgba(201, 169, 97, 0.25)";

function formatTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function ArticleAudioPlayer({ src, title }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => { setDuration(a.duration || 0); setLoading(false); };
    const onEnd = () => setPlaying(false);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onWaiting = () => setLoading(true);
    const onPlaying = () => setLoading(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("durationchange", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("waiting", onWaiting);
    a.addEventListener("playing", onPlaying);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("durationchange", onMeta);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("waiting", onWaiting);
      a.removeEventListener("playing", onPlaying);
    };
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play(); else a.pause();
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    if (clientX == null) return;
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    a.currentTime = pct * duration;
    setCurrent(a.currentTime);
  };

  const skip = (delta) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.min(duration || 0, Math.max(0, a.currentTime + delta));
    setCurrent(a.currentTime);
  };

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <div
      className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-[0_20px_60px_-25px_rgba(26,35,50,0.55)]"
      style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_2} 100%)`,
        border: `1px solid ${GOLD_SOFT}`,
      }}
      data-testid="article-audio"
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      <div className="px-5 sm:px-7 pt-5 sm:pt-6 pb-5 sm:pb-6">
        {/* Label */}
        <div className="flex items-center gap-2 mb-4 sm:mb-5">
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-full"
            style={{ background: GOLD_SOFT, color: GOLD }}
          >
            <Headphones className="w-3.5 h-3.5" />
          </span>
          <span
            className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.22em]"
            style={{ color: GOLD }}
          >
            Listen to this article
          </span>
        </div>

        {/* Controls row */}
        <div className="flex items-center gap-3 sm:gap-5">
          {/* Skip back */}
          <button
            type="button"
            onClick={() => skip(-15)}
            className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full text-white/70 hover:text-white transition-colors"
            style={{ border: `1px solid ${GOLD_SOFT}` }}
            aria-label="Rewind 15 seconds"
            data-testid="audio-skip-back"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Play / Pause */}
          <button
            type="button"
            onClick={toggle}
            className="relative inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full shrink-0 transition-transform active:scale-95 hover:scale-[1.03]"
            style={{
              background: GOLD,
              boxShadow: `0 12px 30px -10px ${GOLD}, inset 0 -2px 0 rgba(0,0,0,0.15)`,
              color: NAVY,
            }}
            aria-label={playing ? "Pause" : "Play"}
            data-testid="audio-play-toggle"
          >
            {playing ? (
              <Pause className="w-6 h-6 sm:w-7 sm:h-7" strokeWidth={2.5} fill={NAVY} />
            ) : (
              <Play className="w-6 h-6 sm:w-7 sm:h-7 translate-x-[1px]" strokeWidth={2.5} fill={NAVY} />
            )}
          </button>

          {/* Skip forward */}
          <button
            type="button"
            onClick={() => skip(30)}
            className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full text-white/70 hover:text-white transition-colors"
            style={{ border: `1px solid ${GOLD_SOFT}` }}
            aria-label="Skip 30 seconds"
            data-testid="audio-skip-fwd"
          >
            <RotateCw className="w-4 h-4" />
          </button>

          {/* Progress + times */}
          <div className="flex-1 min-w-0">
            {title ? (
              <div className="text-white/80 text-xs sm:text-sm font-medium mb-2 line-clamp-1">
                {title}
              </div>
            ) : null}
            <div
              role="slider"
              tabIndex={0}
              aria-label="Seek"
              aria-valuemin={0}
              aria-valuemax={duration || 0}
              aria-valuenow={current}
              onClick={seek}
              onKeyDown={(e) => {
                if (e.key === "ArrowRight") skip(5);
                if (e.key === "ArrowLeft") skip(-5);
              }}
              className="relative h-1.5 rounded-full cursor-pointer group"
              style={{ background: "rgba(255,255,255,0.12)" }}
              data-testid="audio-progress"
            >
              <div
                className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-100"
                style={{ width: `${pct}%`, background: GOLD }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 rounded-full transition-all"
                style={{
                  left: `calc(${pct}% - 7px)`,
                  width: 14,
                  height: 14,
                  background: GOLD,
                  boxShadow: `0 0 0 4px rgba(201,169,97,0.18)`,
                  opacity: duration ? 1 : 0,
                }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] sm:text-xs font-mono tabular-nums text-white/60">
              <span data-testid="audio-current">{formatTime(current)}</span>
              <span data-testid="audio-duration">
                {loading && !duration ? "—:—" : formatTime(duration)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
